import React, { useState, useEffect } from "react";
import Card from "../components/Card/Card";
import CardBody from "../components/Card/CardBody";
import CardHeader from "../components/Card/CardHeader";
import CardFooter from "../components/Card/CardFooter";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Button from "../components/CustomButtons/Button";
import GridContainer from "../components/Grid/GridContainer";
import GridItem from "../components/Grid/GridItem";
import Typography from "@mui/material/Typography";
import CircularLoading from "../components/CircularLoading";
import AlertDialog from "../components/AlertDialog";
import AlertDialogTwoButton from "../components/AlertDialogTwoButton";
import { colors } from "../components/Theme/WebTheme";
import { useSelector, useDispatch } from "react-redux";
import { FirebaseConfig } from "config/FirebaseConfig";

const PowerTranzTransactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paymentSettings, setPaymentSettings] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [error, setError] = useState(null);

    const { t } = useSelector((state) => state.languagedata);
    const dispatch = useDispatch();

    useEffect(() => {
        loadPaymentSettings();
        loadTransactions();
    }, []);

    const loadPaymentSettings = async () => {
        try {
            const response = await fetch(
                `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/powertranz-config`
            );
            if (response.ok) {
                const settings = await response.json();
                setPaymentSettings(settings);
            }
        } catch (error) {
            console.error("Error loading payment settings:", error);
        }
    };

    const loadTransactions = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/powertranz-transactions`
            );
            if (response.ok) {
                const data = await response.json();
                setTransactions(data.transactions || []);
            } else {
                setError("Failed to load transactions");
            }
        } catch (error) {
            console.error("Error loading transactions:", error);
            setError("Error loading transactions");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, currency) => {
        const currencySymbols = {
            PAB: "B/.",
            USD: "$",
            EUR: "€",
        };

        const symbol = currencySymbols[currency] || currency;
        return `${symbol}${parseFloat(amount).toFixed(2)}`;
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "approved":
                return colors.success;
            case "declined":
                return colors.danger;
            case "pending":
                return colors.warning;
            default:
                return colors.info;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "approved":
                return "Approved";
            case "declined":
                return "Declined";
            case "pending":
                return "Pending";
            default:
                return status;
        }
    };

    const handleViewDetails = (transaction) => {
        setSelectedTransaction(transaction);
        setShowDetails(true);
    };

    const handleCloseDetails = () => {
        setShowDetails(false);
        setSelectedTransaction(null);
    };

    // Check if PowerTranz is active
    const isPowerTranzActive = paymentSettings?.powertranz?.active;

    // If PowerTranz is not active, don't render the component
    if (!isPowerTranzActive) {
        return null;
    }

    if (loading) {
        return (
            <GridContainer justify="center">
                <GridItem xs={12} sm={12} md={12}>
                    <CircularLoading />
                </GridItem>
            </GridContainer>
        );
    }

    return (
        <div>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <Card>
                        <CardHeader color="primary">
                            <Typography variant="h6" component="h4">
                                PowerTranz Transactions
                            </Typography>
                        </CardHeader>
                        <CardBody>
                            {error && (
                                <AlertDialog
                                    open={true}
                                    onClose={() => setError(null)}
                                    title="Error"
                                    message={error}
                                />
                            )}

                            {transactions.length === 0 ? (
                                <Typography
                                    variant="body1"
                                    style={{
                                        textAlign: "center",
                                        padding: "20px",
                                    }}
                                >
                                    No PowerTranz transactions found
                                </Typography>
                            ) : (
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                Transaction ID
                                            </TableCell>
                                            <TableCell>Order ID</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>Currency</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map((transaction) => (
                                            <TableRow
                                                key={
                                                    transaction.transactionId ||
                                                    transaction.order_id
                                                }
                                            >
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontFamily:
                                                                "monospace",
                                                        }}
                                                    >
                                                        {transaction.transactionId ||
                                                            transaction.order_id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {transaction.order_id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {transaction.cust_id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {formatCurrency(
                                                            transaction.amount,
                                                            transaction.currency
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {transaction.currency}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            color: getStatusColor(
                                                                transaction.status
                                                            ),
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {getStatusText(
                                                            transaction.status
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(
                                                            transaction.created_at
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        color="info"
                                                        size="sm"
                                                        onClick={() =>
                                                            handleViewDetails(
                                                                transaction
                                                            )
                                                        }
                                                    >
                                                        View Details
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardBody>
                        <CardFooter>
                            <Button
                                color="primary"
                                onClick={loadTransactions}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                        </CardFooter>
                    </Card>
                </GridItem>
            </GridContainer>

            {/* Transaction Details Modal */}
            {showDetails && selectedTransaction && (
                <AlertDialogTwoButton
                    open={showDetails}
                    onClose={handleCloseDetails}
                    title="Transaction Details"
                    message={
                        <div>
                            <Typography variant="h6" gutterBottom>
                                Transaction Information
                            </Typography>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Transaction ID:</strong>
                                        </TableCell>
                                        <TableCell>
                                            {selectedTransaction.transactionId ||
                                                selectedTransaction.order_id}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Order ID:</strong>
                                        </TableCell>
                                        <TableCell>
                                            {selectedTransaction.order_id}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Customer ID:</strong>
                                        </TableCell>
                                        <TableCell>
                                            {selectedTransaction.cust_id}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Amount:</strong>
                                        </TableCell>
                                        <TableCell>
                                            {formatCurrency(
                                                selectedTransaction.amount,
                                                selectedTransaction.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Currency:</strong>
                                        </TableCell>
                                        <TableCell>
                                            {selectedTransaction.currency}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Status:</strong>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color: getStatusColor(
                                                        selectedTransaction.status
                                                    ),
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {getStatusText(
                                                    selectedTransaction.status
                                                )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <strong>Created:</strong>
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(
                                                selectedTransaction.created_at
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {selectedTransaction.powertranz_response && (
                                <>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        style={{ marginTop: "20px" }}
                                    >
                                        PowerTranz Response
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Approved:</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            color: selectedTransaction
                                                                .powertranz_response
                                                                .Approved
                                                                ? colors.success
                                                                : colors.danger,
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {selectedTransaction
                                                            .powertranz_response
                                                            .Approved
                                                            ? "Yes"
                                                            : "No"}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>
                                                        Authorization Code:
                                                    </strong>
                                                </TableCell>
                                                <TableCell>
                                                    {selectedTransaction
                                                        .powertranz_response
                                                        .AuthorizationCode ||
                                                        "N/A"}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Card Brand:</strong>
                                                </TableCell>
                                                <TableCell>
                                                    {selectedTransaction
                                                        .powertranz_response
                                                        .CardBrand || "N/A"}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>RRN:</strong>
                                                </TableCell>
                                                <TableCell>
                                                    {selectedTransaction
                                                        .powertranz_response
                                                        .RRN || "N/A"}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>
                                                        Response Code:
                                                    </strong>
                                                </TableCell>
                                                <TableCell>
                                                    {selectedTransaction
                                                        .powertranz_response
                                                        .IsoResponseCode ||
                                                        "N/A"}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>
                                                        Response Message:
                                                    </strong>
                                                </TableCell>
                                                <TableCell>
                                                    {selectedTransaction
                                                        .powertranz_response
                                                        .ResponseMessage ||
                                                        "N/A"}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>
                                                        Total Amount:
                                                    </strong>
                                                </TableCell>
                                                <TableCell>
                                                    {selectedTransaction
                                                        .powertranz_response
                                                        .TotalAmount || "N/A"}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>
                                                        Currency Code:
                                                    </strong>
                                                </TableCell>
                                                <TableCell>
                                                    {selectedTransaction
                                                        .powertranz_response
                                                        .CurrencyCode || "N/A"}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </>
                            )}
                        </div>
                    }
                    buttonText="Close"
                    onButtonClick={handleCloseDetails}
                />
            )}
        </div>
    );
};

export default PowerTranzTransactions;
